import { useState } from 'react';
import type { ReactElement, VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import { useOrganizationDomains } from '../../hooks/api/organization/useOrganizationDomains';
import { ErrorAlert } from '../generic';
import Button from '../generic/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import List from '../generic/List';
import Loader from '../generic/Loader';

const SSODomainsCard: VoidFunctionComponent = () => {
  const [isCopied, setIsCopied] = useState(false);
  const { data: domains = [], isLoading, error } = useOrganizationDomains(window.user?.organizationId as number);

  const handleCopyToClipboard = async (): Promise<void> => {
    let domainsInfo = '';
    if (domains.length > 0) {
      domainsInfo = domains.map(({ domain, ssoMethod }) => `${domain}: ${ssoMethod}`).join('\n');
    } else {
      domainsInfo = 'None';
    }

    await navigator.clipboard.writeText(domainsInfo);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  let content: ReactElement;
  if (error) {
    content = <ErrorAlert status={{ errors: [error] }} withMargin />;
  } else if (isLoading) {
    content = <Loader />;
  } else if (domains.length === 0) {
    content = <p className="margin-left-md margin-bottom-md">None</p>;
  } else {
    content = (
      <List isBorderless>
        <List.Row hasTightPadding hasTightVerticalPadding left={<b>Domain</b>} right={<b>SSO Method</b>} />
        {domains.map(({ domain, ssoMethod }) => (
          <List.Row key={domain} hasTightPadding hasTightVerticalPadding left={domain} right={ssoMethod} />
        ))}
      </List>
    );
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>
        <Flex direction="row" align="center" justify="space-between">
          <div>SSO Domains Claimed</div>
          <Button onClick={handleCopyToClipboard} size="micro" type="outline">
            {isCopied ? 'Copied!' : 'Copy'}
          </Button>
        </Flex>
      </Card.Header>
      <Card.Content>
        {content}
      </Card.Content>
    </Card>
  );
};

export default SSODomainsCard;
